import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

const SELECTED_PLAN_KEY = 'icip_selected_plan';

/**
 * Blocks the registration route unless a valid self-serve plan has been selected and cached in
 * sessionStorage (set from the home pricing grid / plan prompt). No one may land on /register and
 * proceed plan-less — a direct visit, a shared link, or any navigation without a plan is redirected
 * to the home page (where plans are chosen).
 *
 * "Valid" matches RegisterComponent.loadSelectedPlan(): the stored value must be parseable JSON
 * carrying a non-empty planName. A stale/corrupt entry is treated as "no plan".
 */
export const PlanSelectedGuard: CanActivateFn = (): boolean | UrlTree => {
  const router = inject(Router);

  const redirectHome = (): UrlTree => router.createUrlTree(['/'], { fragment: 'pricing' });

  let raw: string | null;
  try {
    raw = sessionStorage.getItem(SELECTED_PLAN_KEY);
  } catch {
    // Storage blocked/unavailable — cannot confirm a plan, so don't allow registration.
    return redirectHome();
  }

  if (!raw) {
    return redirectHome();
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.planName) {
      return redirectHome();
    }
  } catch {
    return redirectHome();
  }

  return true;
};
